import { Alert, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DrawerContent, DrawerFooter, Empty, LabelValue, Loader } from "@zfp-akademie-a-s/zfp-design";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { ProposedSolutionDeleteDialog } from "Components/Analyses/GoalsFulfillment/ProposedSolutionDeleteDialog";
import { invalidateProposalQueries } from "Components/Analyses/GoalsFulfillment/invalidateProposalQueries";
import { useDeleteContractProposal, useGetContractProposal } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { ContractProposalDetailInvestment } from "Infrastructure/ApiZfp/Schema/contractProposalDetailInvestment";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import { InvestmentsNsureSelectionCard } from "./InvestmentsNsureSelectionCard";
import { buildInitialNsureSelection } from "./helpers";

const EMPTY_VALUE = "—";

type Props = {
  readonly proposedSolutionId: string;
  readonly onClose: () => void;
};

export const ProposedSolutionNsureReadOnly = ({ proposedSolutionId, onClose }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();

  const {
    data: solutionData,
    isError,
    isFetching,
  } = useGetContractProposal(analysisId, versionId, proposedSolutionId, {
    query: { enabled: !!proposedSolutionId },
  });

  const investmentData = solutionData as ContractProposalDetailInvestment | undefined;

  const selection = useMemo(() => buildInitialNsureSelection(true, investmentData), [investmentData]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutate: deleteMutation, isPending: isDeletePending } = useDeleteContractProposal({
    mutation: {
      onSuccess: () => invalidateProposalQueries(queryClient, analysisId, versionId),
      onError: () => toast.error(t(Resources.AnalysisDetail.Toast.ActionError)),
    },
  });

  const deleteSolution = () => {
    deleteMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId },
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          onClose();
        },
        onError: () => setIsDeleteDialogOpen(false),
      },
    );
  };

  const formatAmount = (value: number | null | undefined, suffix = "") =>
    value == null ? EMPTY_VALUE : `${formatCurrencyWithoutCode(value)} ${t(Resources.Common.CZKSuffix)}${suffix}`;
  const formatPercentage = (value: number | null | undefined, suffix = "") =>
    value == null ? EMPTY_VALUE : `${value.toLocaleString("cs-CZ")} %${suffix}`;

  if (!solutionData || isFetching) {
    return isError ? (
      <Empty
        title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
        text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
        iconSize={40}
        icon={<ErrorIcon sx={{ color: "grey.400" }} />}
      />
    ) : (
      <Loader />
    );
  }

  return (
    <>
      <DrawerContent>
        <Stack spacing={Spacing.XL} pb={Spacing.XXL}>
          <Alert severity="warning">{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.ReadOnly.InfoAlert)}</Alert>

          {selection ? (
            <InvestmentsNsureSelectionCard selection={selection} />
          ) : (
            <Alert severity="grey">{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Details.LoadError)}</Alert>
          )}

          <Stack spacing={Spacing.L} px={Spacing.L}>
            <Typography variant="body1" fontWeight={500}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.ReadOnly.Basics)}
            </Typography>
            <Grid container rowSpacing={Spacing.XXL} columnSpacing={Spacing.XL}>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Partner)}
                  value={investmentData?.partner?.name ?? EMPTY_VALUE}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Product)}
                  value={investmentData?.productName ?? EMPTY_VALUE}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                  value={
                    investmentData?.currentSolutionHorizon == null ? EMPTY_VALUE : inflectYears(investmentData.currentSolutionHorizon, t)
                  }
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeDeposit)}
                  value={formatAmount(investmentData?.oneTimeAmount?.value)}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                  value={formatAmount(investmentData?.regularAmount?.value, `/${t(Resources.Common.Month)}`)}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                  value={formatPercentage(investmentData?.appreciationRate, " p.a.")}
                />
              </Grid>
            </Grid>
          </Stack>

          <Divider />

          <Stack spacing={Spacing.L} px={Spacing.L}>
            <Typography variant="body1" fontWeight={500}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Fees)}
            </Typography>
            <Grid container rowSpacing={Spacing.XXL} columnSpacing={Spacing.XL}>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFee)}
                  value={formatPercentage(investmentData?.entryFee)}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeFee)}
                  value={formatAmount(investmentData?.oneTimeFee?.value)}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFee)}
                  value={formatPercentage(investmentData?.managementFee)}
                />
              </Grid>
              <Grid size={4}>
                <LabelValue
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ExitFee)}
                  value={formatPercentage(investmentData?.exitFee)}
                />
              </Grid>
            </Grid>
          </Stack>

          {investmentData?.note && (
            <>
              <Divider />
              <Stack spacing={Spacing.L}>
                <Typography variant="body1" fontWeight={500}>
                  {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {investmentData.note}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </DrawerContent>

      <DrawerFooter
        startAction={
          <Button variant="text" color="error" onClick={() => setIsDeleteDialogOpen(true)} disabled={isDeletePending}>
            {t(Resources.Common.Delete)}
          </Button>
        }
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
      />

      <ProposedSolutionDeleteDialog
        isOpen={isDeleteDialogOpen}
        isPending={isDeletePending}
        onClose={() => !isDeletePending && setIsDeleteDialogOpen(false)}
        onConfirm={deleteSolution}
      />
    </>
  );
};
